/**
 * Anthropic list prices, $/MTok (input, output). Ported from claude-ledger
 * analyze.py. A model id missing here prices at $0 — the parser logs it.
 * Cache write is 1.25× (5m) / 2× (1h) input. Cache read is 0.1× input unless
 * CACHE_READ overrides it.
 */
export const PRICES: Record<string, [number, number]> = {
	"claude-fable-5-1": [10, 50],
	"claude-mythos-5-1": [10, 50],
	"claude-fable-5": [10, 50],
	"claude-mythos-5": [10, 50],
	"claude-opus-5-5": [4, 20],
	"claude-opus-5": [5, 25],
	"claude-opus-4-8": [5, 25],
	"claude-opus-4-7": [5, 25],
	"claude-opus-4-6": [5, 25],
	"claude-opus-4-5": [5, 25],
	"claude-sonnet-5": [2, 10],
	"claude-sonnet-4-6": [3, 15],
	"claude-sonnet-4-5": [3, 15],
	"claude-haiku-4-5": [1, 5],
};

/** Models whose cache read is a flat $/MTok rather than 0.1× input. */
export const CACHE_READ: Record<string, number> = {
	"claude-fable-5-1": 0.25,
	"claude-mythos-5-1": 0.25,
};

/** Fast mode (`usage.speed == "fast"`) prices the same model at premium rates. */
export const FAST_PRICES: Record<string, [number, number]> = {
	"claude-opus-5-5": [8, 40],
	"claude-opus-5": [10, 50],
	"claude-opus-4-8": [10, 50],
};

const ALIASES: Record<string, string> = {
	opus: "claude-opus-5",
	sonnet: "claude-sonnet-5",
	haiku: "claude-haiku-4-5",
};

/** OpenRouter's Anthropic ids, e.g. `anthropic/claude-sonnet-4.5`. */
const OPENROUTER_ANTHROPIC = /^anthropic\/(claude-[a-z]+-\d+)\.(\d+)$/;

/**
 * Drop dated snapshot suffixes and the `[1m]` marker, map OpenRouter Anthropic
 * ids onto the Claude id (`anthropic/claude-sonnet-4.5` → `claude-sonnet-4-5`),
 * then apply short aliases. Other `vendor/model` ids are left as they are.
 */
export function normModel(model: string | undefined): string {
	const stripped = (model ?? "")
		.replace("[1m]", "")
		.replace(/-\d{8}$/, "")
		.replace(OPENROUTER_ANTHROPIC, "$1-$2");
	return ALIASES[stripped] ?? stripped;
}

type Usage = {
	input_tokens?: number;
	output_tokens?: number;
	cache_read_input_tokens?: number;
	cache_creation_input_tokens?: number;
	cache_creation?: {
		ephemeral_5m_input_tokens?: number;
		ephemeral_1h_input_tokens?: number;
	};
	speed?: string;
};

/** Anthropic turn cost from a Claude Code usage block. $0 when the model is unpriced. */
export function claudeCost(model: string, usage: Usage): number {
	const list = PRICES[model];
	if (!list) return 0;
	const fast = usage.speed === "fast" ? FAST_PRICES[model] : undefined;
	const [input, output] = fast ?? list;
	const cacheRead = CACHE_READ[model] ?? input * 0.1;
	const created = usage.cache_creation ?? {};
	let write5m = created.ephemeral_5m_input_tokens;
	let write1h = created.ephemeral_1h_input_tokens;
	if (write5m == null && write1h == null) {
		write5m = usage.cache_creation_input_tokens ?? 0;
		write1h = 0;
	}
	return (
		((usage.input_tokens ?? 0) * input +
			(usage.output_tokens ?? 0) * output +
			(write5m ?? 0) * input * 1.25 +
			(write1h ?? 0) * input * 2 +
			(usage.cache_read_input_tokens ?? 0) * cacheRead) /
		1e6
	);
}
